import { Button, PreviewPanel, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, ExternalLink, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type DocumentLineageResponse,
  errorMessage,
  fetchChatAttachment,
  fetchChatAttachmentPreview,
  listSessionDocumentsOptions,
  listSpaceDocumentsQueryKey,
  shareSpaceDocumentMutation,
} from "@/api";
import { usePreviewPanelLabels } from "@/components/designLabels";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { saveBlob } from "@/lib/download";
import { DocumentPreview } from "./documents/DocumentPreview";
import {
  displayName,
  extensionOf,
  imageTypeOf,
  type OpenDocument,
  versionsNewestFirst,
} from "./documents/documentInfo";
import { ImageDocumentPreview } from "./documents/ImageDocumentPreview";
import { chatAttachmentFile } from "./documents/previewFile";
import { TextDocumentPreview } from "./documents/TextDocumentPreview";
import { type TextKind, textKindOf } from "./documents/textDocuments";

/** Documents the assistant created or revised in this chat. */
export function useChatDocuments(sessionUuid: string | undefined) {
  return useQuery({
    ...listSessionDocumentsOptions({ path: { session_uuid: sessionUuid ?? "" } }),
    enabled: Boolean(sessionUuid),
    select: (r) => r.data,
  });
}

function useFileActions() {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const toast = useToast();
  return {
    download: async (attachmentUuid: string, filename: string) => {
      try {
        const file = await fetchChatAttachment(attachmentUuid);
        saveBlob(file.blob, file.filename ?? filename);
      } catch (error) {
        toast.error(errorMessage(error, t("downloadFailed")));
      }
    },
    /**
     * PDF and Office files open as a PDF. Others open as the original file shown as *type*: text files
     * (Markdown, CSV, HTML) as plain text, never run; images as their own image type.
     */
    openInNewTab: async (attachmentUuid: string, type?: string) => {
      // Open the tab synchronously (popup blockers), then point it at the file once fetched.
      const tab = window.open("", "_blank");
      try {
        const blob = type
          ? new Blob([(await fetchChatAttachment(attachmentUuid)).blob], { type })
          : (await fetchChatAttachmentPreview(attachmentUuid)).blob;
        if (tab) tab.location.href = URL.createObjectURL(blob);
      } catch (error) {
        tab?.close();
        toast.error(errorMessage(error, t("previewFailed")));
      }
    },
  };
}

/** Shares the version shown into the chat's space, as a snapshot members can open. */
function ShareWithSpaceButton({
  space,
  attachmentUuid,
}: Readonly<{ space: { uuid: string; name: string }; attachmentUuid: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const path = { space_uuid: space.uuid };
  const share = useMutation({
    ...shareSpaceDocumentMutation(),
    onSuccess: () => toast.success(t("sharedWithSpace", { space: space.name })),
    onError: (error) => toast.error(errorMessage(error, t("shareFailed"))),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listSpaceDocumentsQueryKey({ path }) }),
  });
  return (
    <Button
      variant="secondary"
      size="sm"
      fullWidth
      leadingIcon={<Users size={14} />}
      loading={share.isPending}
      onClick={() => share.mutate({ path, body: { attachmentUuid } })}
    >
      {t("shareWithSpace", { space: space.name })}
    </Button>
  );
}

/** The open version in the viewer for its kind: text in the browser, images as themselves, else server-rendered pages. */
function ShownDocument({
  attachmentUuid,
  title,
  textKind,
  imageType,
  view,
}: Readonly<{
  attachmentUuid: string;
  title: string;
  textKind?: TextKind;
  imageType?: string;
  view: "source" | "preview";
}>) {
  const file = chatAttachmentFile(attachmentUuid);
  if (textKind) {
    return <TextDocumentPreview file={file} kind={textKind} title={title} view={view} />;
  }
  if (imageType) return <ImageDocumentPreview attachmentUuid={attachmentUuid} type={imageType} title={title} />;
  return <DocumentPreview file={file} />;
}

/**
 * Side panel for the chat's documents: the pages of the open document (any version),
 * its version history, and download. Remount it (key) when `open` changes to reset the tab.
 */
export function ChatDocumentsPanel({
  documents,
  open,
  onOpen,
  onClose,
  shareTo,
}: Readonly<{
  documents: DocumentLineageResponse[];
  open: OpenDocument;
  onOpen: (open: OpenDocument) => void;
  onClose: () => void;
  /** The chat's space, when I may share this chat's documents into it. */
  shareTo?: { uuid: string; name: string };
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const labels = usePreviewPanelLabels();
  const files = useFileActions();
  const [tab, setTab] = useState("preview");
  const found = documents.findIndex((d) => d.uuid === open.lineageUuid);
  const index = found >= 0 ? found : documents.length - 1;
  const doc = documents[index];
  if (!doc) return null;
  const versions = versionsNewestFirst(doc);
  const latest = versions[0];
  const shown = versions.find((v) => v.attachmentUuid === open.attachmentUuid) ?? latest;
  const ext = shown ? extensionOf(shown.filename, doc.documentType) : "";
  const textKind = textKindOf(ext);
  const imageType = imageTypeOf(ext);
  const tabs = [{ value: "preview", label: t("tabs.preview") }];
  if (textKind) tabs.push({ value: "source", label: t("tabs.source") });
  tabs.push({ value: "versions", label: t("tabs.versions") });

  return (
    <PreviewPanel
      // Documents are page-shaped: start wider than the default column.
      defaultWidth={560}
      maxWidth={760}
      labels={labels}
      artifacts={documents.map((d) => ({
        key: d.uuid,
        name: displayName(d),
        meta: t("versionShort", { version: d.currentVersion }),
      }))}
      activeIndex={index}
      onSelectArtifact={(i) => onOpen({ lineageUuid: documents[i].uuid })}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
      onClose={onClose}
      actions={
        shown && (
          <>
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              leadingIcon={<ExternalLink size={14} />}
              onClick={() => void files.openInNewTab(shown.attachmentUuid, textKind ? "text/plain" : imageType)}
            >
              {t("openInNewTab")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              leadingIcon={<Download size={14} />}
              onClick={() => void files.download(shown.attachmentUuid, shown.filename)}
            >
              {t("download")}
            </Button>
            {shareTo && <ShareWithSpaceButton space={shareTo} attachmentUuid={shown.attachmentUuid} />}
          </>
        )
      }
    >
      {tab !== "versions" && shown ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <Text variant="caption" tone="tertiary">
              {t("summary", {
                version: shown.version,
                total: versions.length,
                date: formatDateTime(shown.createdAt),
              })}
            </Text>
            {shown !== latest && (
              <Button variant="text" size="sm" onClick={() => onOpen({ lineageUuid: doc.uuid })}>
                {t("showLatest")}
              </Button>
            )}
          </div>
          <ShownDocument
            key={shown.attachmentUuid}
            attachmentUuid={shown.attachmentUuid}
            title={shown.filename}
            textKind={textKind}
            imageType={imageType}
            view={tab === "source" ? "source" : "preview"}
          />
        </div>
      ) : (
        <ol className="space-y-2">
          {versions.map((v) => (
            <li
              key={v.attachmentUuid}
              className="flex items-start justify-between gap-3 rounded-lg border border-border-subtle bg-surface-primary px-3 py-2.5"
            >
              <div className="min-w-0">
                <Text>{t("versionLong", { version: v.version })}</Text>
                <Text variant="caption" tone="tertiary">
                  {v.changeSummary ?? formatDateTime(v.createdAt)}
                </Text>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button
                  variant="text"
                  size="sm"
                  aria-label={t("previewVersion", { version: v.version })}
                  onClick={() => onOpen({ lineageUuid: doc.uuid, attachmentUuid: v.attachmentUuid })}
                >
                  {t("preview")}
                </Button>
                <Button
                  variant="text"
                  size="sm"
                  aria-label={t("downloadVersion", { version: v.version })}
                  onClick={() => void files.download(v.attachmentUuid, v.filename)}
                >
                  {t("download")}
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </PreviewPanel>
  );
}
